// Shared utilities for Layer B (runtime/RTDB-backed) stores.
//
// Centralizes three patterns that used to be copy-pasted across pos.jobs,
// pos.receipts, pos.tableStates, pos.soldout, pos.billsNumber:
//
// 1. `knownKeys` dedup set (so RTDB replay doesn't rewrite IDB rows that
//    already match what was restored).
// 2. IDB restore (per-row or single-blob) at boot.
// 3. "Wait for the first RTDB settle" — replaces the `setTimeout(4000)`
//    workaround in pos.jobs by using `onValue` once to detect when the
//    initial child-replay has finished.

import { onValue, type Query } from "firebase/database";
import { bucket, type Bucket, offlineBucketNames } from "~/services/storage/offlineBuckets";

type BucketName = keyof typeof offlineBucketNames;

export function getStoreBucket(name: BucketName, storeId: string | null | undefined): Bucket | null {
  if (!storeId) return null;
  return bucket(offlineBucketNames[name](String(storeId)));
}

/**
 * Runtime IDB mirroring (jobs, receipts, table state, sold-out) is only the
 * Main Cashier's job. The Main Cashier owns the offline-first runtime cache;
 * Terminal / 2nd cashier / readonly runtimes read RTDB live and must NOT write
 * the runtime mirror — their IDB would be partial/racey and could be promoted
 * into a bad offline boot. Gate every runtime mirror write through this.
 *
 * `runMode === 'cashier'` is the Main Cashier. usePosShopStore is auto-imported
 * by Nuxt; the try/catch keeps boot-time callers (before Pinia is ready) safe.
 */
export function isMainCashierRuntime(): boolean {
  try {
    return usePosShopStore().runMode === "cashier";
  } catch {
    return false;
  }
}

/**
 * Resolves on the first `onValue` settle from the RTDB ref, or after
 * `timeoutMs` — whichever comes first. The temporary whole-node listener is
 * detached once settled (ongoing updates are still handled via the caller's
 * `onChildAdded`/`onChildChanged`/`onValue` flow).
 *
 * Use for bootOnline paths that previously gated `loaded` on a hardcoded
 * timer. Now `loaded` resolves on actual signal — falling back to timeout
 * only when RTDB never responds (empty path, slow network).
 */
export function waitForFirstRTDBSettle(ref: Query, timeoutMs = 3000): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    let unsub: (() => void) | null = null;

    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub?.();
      resolve();
    };

    const timer = setTimeout(done, timeoutMs);

    try {
      unsub = onValue(ref, () => done(), () => done());
      // The SDK can deliver a cached value synchronously inside the attach
      // call — done() then ran with unsub still null, leaking a whole-node
      // listener that re-downloads the entire ref on every change.
      if (settled) {
        unsub();
        unsub = null;
      }
    } catch {
      done();
    }
  });
}

/**
 * Per-row IDB restore. Returns the restored entries + a `knownKeys` Set
 * the caller can keep around for write-side dedup.
 *
 * - When `restoreData = true` (offline boot): all entries are returned so
 *   the caller can populate Pinia immediately.
 * - When `restoreData = false` (online boot): only keys are loaded into
 *   `knownKeys` so subsequent RTDB-replay writes can be deduped.
 */
export async function loadPerRowBucket<T = any>(
  b: Bucket | null,
  restoreData: boolean
): Promise<{ entries: [string, T][]; knownKeys: Set<string> }> {
  const knownKeys = new Set<string>();
  if (!b) return { entries: [], knownKeys };

  try {
    if (restoreData) {
      const all = await b.entries<T>();
      for (const [k] of all) knownKeys.add(String(k));
      return { entries: all.map(([k, v]) => [String(k), v] as [string, T]), knownKeys };
    } else {
      const keys = await b.keys<IDBValidKey>();
      for (const k of keys) knownKeys.add(String(k));
      return { entries: [], knownKeys };
    }
  } catch (err) {
    console.warn("[layerB] loadPerRowBucket failed", err);
    return { entries: [], knownKeys };
  }
}

/**
 * Single-blob IDB restore (for stores like tableStates / soldout / splitBill
 * that mirror one logical document).
 */
export async function loadSingleRow<T = any>(
  b: Bucket | null,
  key: string
): Promise<T | null> {
  if (!b) return null;
  try {
    const value = await b.get<T>(key);
    return (value && typeof value === "object") ? value : null;
  } catch (err) {
    console.warn(`[layerB] loadSingleRow(${key}) failed`, err);
    return null;
  }
}

/**
 * Write-side helper. Skips IDB write when the key is already known —
 * prevents repeated writes from RTDB's initial child replay.
 *
 * Mutates `knownKeys` so subsequent calls with the same key short-circuit.
 */
export function mirrorRowToIDB<T>(
  b: Bucket | null,
  knownKeys: Set<string>,
  key: string,
  value: T
): void {
  // Only the Main Cashier mirrors runtime rows to IDB (jobs/receipts).
  if (!isMainCashierRuntime()) return;
  if (!key || !b) return;
  if (knownKeys.has(key)) return;
  knownKeys.add(key);
  void b.set(key, value);
}
